import { useEffect, useMemo } from 'react'

import { MultiSlideCarousel, Spinner } from '@percent/lemonade'

export interface InfiniteScrollingCarouselProps<T> {
  title: string
  data: T[]
  itemsPerPage: number
  renderItem: (item: T) => React.ReactNode
  keyExtractor?: (item: T, index: number) => string | number
  hasNextPage?: boolean
  fetchNextPage: () => void
  isFetchingNextPage?: boolean
  action?: React.ReactNode
  /** MultiSlideCarousel layout options */
  slideGap?: string
  /** MultiSlideCarousel classNames (header, title, nav wrapper) */
  headerClassName?: string
  titleClassName?: string
  navWrapperClassName?: string
}

export function InfiniteScrollingCarousel<T>({
  title,
  data,
  itemsPerPage,
  renderItem,
  keyExtractor,
  hasNextPage = false,
  fetchNextPage,
  isFetchingNextPage = false,
  action,
  slideGap = '2.5rem',
  headerClassName,
  titleClassName,
  navWrapperClassName
}: InfiniteScrollingCarouselProps<T>) {
  const items = useMemo(
    () =>
      data.map((item, index) => ({
        id: String(keyExtractor ? keyExtractor(item, index) : index),
        content: renderItem(item)
      })),
    [data, keyExtractor, renderItem]
  )

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return

    if (data.length < itemsPerPage * 2) {
      fetchNextPage()
    }
  }, [data.length, itemsPerPage, hasNextPage, isFetchingNextPage, fetchNextPage])

  const description =
    action || isFetchingNextPage ? (
      <>
        {action}
        {isFetchingNextPage ? <Spinner size="xs" /> : null}
      </>
    ) : undefined

  return (
    <MultiSlideCarousel
      title={title}
      description={description}
      items={items}
      itemsPerPage={{ xs: itemsPerPage }}
      scrollByPage={false}
      slideGap={slideGap}
      loop={false}
      headerClassName={headerClassName}
      titleClassName={titleClassName}
      navWrapperClassName={navWrapperClassName}
    />
  )
}
